import { sidebarQueryKeys } from '@features/sidebar/api/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { PlusIcon } from '../../shared/icons/icons'
import SelectorFooter from '@features/shared/components/SelectorFooter'
import { useCreateTableDialogStore } from '@features/tables/stores/useCreateTableDialogStore'
import { useCanCreateTables } from '@features/tables/hooks/useCanCreateTables'
import { useSaveTableOrganization, useTableOrganization } from '../hooks'
import type { FolderSchema, TableSidebarTree } from '../api/contracts'

const EMPTY_ORGANIZATION: TableSidebarTree = {
  folders: [],
  unorganized_table_order: [],
  flat_table_order: [],
}

interface TableSidebarFooterProps {
  foldersEnabled?: boolean
  onEnableFolders?: () => void
  /** Add table was chosen: the menu closes, as for a table, under the Add Table panel. */
  onAddTable?: () => void
}

function getNextFolderName(folders: FolderSchema[]): string {
  const rootFolderNames = new Set(
    folders.filter((folder) => folder.parent_id === null).map((folder) => folder.name)
  )

  if (!rootFolderNames.has('New folder')) return 'New folder'

  let index = 2
  while (rootFolderNames.has(`New folder ${index}`)) index += 1
  return `New folder ${index}`
}

export default function TableSidebarFooter({
  foldersEnabled = true,
  onEnableFolders,
  onAddTable,
}: TableSidebarFooterProps) {
  const { data: organization, isLoading } = useTableOrganization()
  const saveMutation = useSaveTableOrganization()
  const queryClient = useQueryClient()
  const openCreateTable = useCreateTableDialogStore((state) => state.openCreateTable)
  const canCreateTables = useCanCreateTables()

  const currentOrganization = organization?.tree ?? EMPTY_ORGANIZATION

  const handleAddFolder = () => {
    const rootFolderCount = currentOrganization.folders.filter(
      (folder) => folder.parent_id === null
    ).length
    const newFolder: FolderSchema = {
      id: crypto.randomUUID(),
      name: getNextFolderName(currentOrganization.folders),
      parent_id: null,
      sort_order: rootFolderCount,
      tables: [],
    }
    const nextOrganization: TableSidebarTree = {
      ...currentOrganization,
      folders: [...currentOrganization.folders, newFolder],
    }

    queryClient.setQueryData(sidebarQueryKeys.tableOrganization, {
      tree: nextOrganization,
      tables: organization?.tables ?? [],
      updated_at: organization?.updated_at ?? null,
    })
    onEnableFolders?.()
    saveMutation.mutate(nextOrganization)
  }

  return (
    <SelectorFooter
      spacing="sidebar"
      actions={[
        // Only for a user who can create tables; without Add folder too, the footer goes.
        ...(canCreateTables
          ? [
              {
                id: 'add-table',
                label: 'Add table',
                icon: <PlusIcon size={12} aria-hidden="true" />,
                onClick: () => {
                  openCreateTable()
                  onAddTable?.()
                },
              },
            ]
          : []),
        ...(foldersEnabled
          ? [
              {
                id: 'add-folder',
                label: 'Add folder',
                icon: <PlusIcon size={12} aria-hidden="true" />,
                disabled: isLoading || saveMutation.isPending,
                onClick: handleAddFolder,
              },
            ]
          : []),
      ]}
    />
  )
}
